// Library
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'

// Routes
import { PATH_LOGIN } from 'routes/paths'

// Use
import useForm from 'use/form'
import { useDesktop, useTablet } from 'use/media'
import useQuery from 'use/query'

// Helper
import { resolveErrorMessage } from 'helpers'

// Services
import dialog from 'services/dialog'
import message from 'services/message'

// Ui
import Row from 'ui/Row'
import Col from 'ui/Col'
import LoginBanner from 'ui/LoginBanner'
import Box from 'ui/Box'
import Heading from 'ui/Heading'
import Button from 'ui/Button'
import InputPassword from 'ui/Form/InputPassword'
// Api
import { resetPassword } from 'api/password'
import Text from 'ui/Text'
import ShowInputPassword from 'ui/Form/ShowInputPassword'

// Validation schema
const validationSchema = yup.object().shape({
  newPassword: yup
    .string()
    .matches(
      `^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{9,}$`,
      () => (
        <>
          <div></div>
        </>
      )
    )
    .required('The password field is required'),
  newPasswordConfirm: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], `The password confirm don't match`)
    .required('The confirm password field is required'),
})

// InitialForm
const initialForm = {
  newPassword: '',
  newPasswordConfirm: '',
}

const ResetPassword = () => {
  // State
  const history = useHistory()
  const isDesktop = useDesktop()
  const isTablet = useTablet()
  const { code } = useQuery()
  const [loading, setLoading] = useState(false)
  // UseFormHook
  const { form, errors, validate, onChange, onBlur } = useForm({
    schema: validationSchema,
    initialForm,
  })

  // Event
  const onEnterPress = (e) => {
    if (e.keyCode === 13 && !loading) {
      e.preventDefault()
      onClickSubmit()
    }
  }

  const onClickSubmit = async () => {
    // Validation
    try {
      await validate()
    } catch (error) {
      return
    }

    setLoading(true)

    // Call api
    try {
      await resetPassword(form, code)
      dialog.alert({
        title: 'New password saved!',
        message:
          'You have successfully created a new password. Thank you for keeping your account secure',
        onConfirm: ({ setVisible }) => {
          setVisible(false)
          history.replace(PATH_LOGIN)
        },
      })
    } catch (error) {
      message.error(await resolveErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  // Render
  return (
    <Row noGutters style={{ height: isDesktop ? '100vh' : 'auto' }}>
      <Col xs={{ span: 12, vcenter: true }} md={{ span: 7, vcenter: true }}>
        <LoginBanner></LoginBanner>
      </Col>
      <Col
        xs={{ span: 10, offset: 1, vcenter: true }}
        sm={{ span: 8, offset: 2, vcenter: true }}
        md={{ span: 4, offset: 0, vcenter: true }}
      >
        <Box
          className={`${isTablet && 'w-100'}`}
          style={{ backgroundColor: 'unset' }}
        >
          <Heading
            size={isDesktop ? 24 : 20}
            paddingTop={10}
            paddingBottom={10}
          >
            Set up a new password
          </Heading>
          <Text
            size={isDesktop ? 16 : 14}
            paddingTop={5}
            paddingBottom={0}
            color="#000000"
            bold
          >
            Password must be a combination of:
          </Text>
          <InputPassword
            name="newPassword"
            label="New password"
            placeholder="Enter password here"
            value={form.newPassword}
            errors={errors}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onEnterPress}
            style={{ width: '100%' }}
          />
          <ShowInputPassword
            name="newPasswordConfirm"
            label="Re-type password"
            placeholder="Re-type password here"
            value={form.newPasswordConfirm}
            errors={errors}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onEnterPress}
            style={{ width: '100%' }}
          />
          <Row>
            <Col xs={6}>
              <Button block loading={loading} onClick={onClickSubmit}>
                Save and login
              </Button>
            </Col>
          </Row>
        </Box>
      </Col>
    </Row>
  )
}

export default ResetPassword
